import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { App as AntdApp, Button, Card, Checkbox, DatePicker, Descriptions, Drawer, Empty, Flex, Input, Select, Space, Table, Tag, Timeline, Typography } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { DownloadOutlined, EyeOutlined, MailOutlined, RedoOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import type { EmailDetails, EmailListItem, EmailListQuery, EmailStatus } from '../../shared/services/api'
import { getApiService } from '../../shared/services/api'
import { PageHeader } from '../../shared/ui/PageHeader'
import { emailStatusOptions, emailStatusView } from '../../shared/utils/emailStatus'
import { appTimezoneNow, formatInAppTimezone, getAppTimezoneOffsetMinutes } from '../../shared/utils/timezone'
import { useAuthContext } from '../../shared/contexts/AuthContext'
import { useSearchParams } from 'react-router-dom'

const initialQuery: EmailListQuery = { page: 1, pageSize: 25 }

export function EmailsPage() {
  const { message } = AntdApp.useApp()
  const api = useMemo(() => getApiService(), [])
  const { user } = useAuthContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const openedFromUrl = useRef<number | null>(null)
  const [query, setQuery] = useState(initialQuery)
  const [items, setItems] = useState<EmailListItem[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const latestRequest = useRef(0)
  const [details, setDetails] = useState<EmailDetails | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [exporting, setExporting] = useState(false)

  const load = useCallback(async () => {
    const request = ++latestRequest.current
    setLoading(true)
    try {
      const response = await api.listEmails(query)
      if (request !== latestRequest.current) return
      setItems(response.data.emails)
      setCount(response.data.count)
    } catch { if (request === latestRequest.current) message.error('Не удалось загрузить отправки') }
    finally { if (request === latestRequest.current) setLoading(false) }
  }, [api, query])
  useEffect(() => { void load() }, [load])

  const openDetails = useCallback(async (id: number) => {
    setDetailsLoading(true)
    setDetails(null)
    try { setDetails((await api.emailDetails(id)).data) }
    catch { message.error('Не удалось загрузить письмо') }
    finally { setDetailsLoading(false) }
  }, [api, message])

  useEffect(() => {
    const id = Number(searchParams.get('open'))
    if (!Number.isInteger(id) || id < 1 || openedFromUrl.current === id) return
    openedFromUrl.current = id
    void openDetails(id)
  }, [openDetails, searchParams])

  const columns: ColumnsType<EmailListItem> = [
    { title: '№', dataIndex: 'id', width: 82, render: (value) => <Typography.Text strong>{value}</Typography.Text> },
    { title: 'Получатель', dataIndex: 'email_address', width: 260, ellipsis: true, render: (value, row) => <Flex vertical gap={0}><Typography.Text copyable>{value}</Typography.Text><Typography.Text type="secondary">{row.api_key_name || 'Приложение удалено'}</Typography.Text></Flex> },
    { title: 'Тема', dataIndex: 'subject', minWidth: 260, ellipsis: true },
    { title: 'Состояние', dataIndex: 'status', width: 205, render: (status: EmailStatus) => <StatusTag status={status} /> },
    { title: 'Создано', dataIndex: 'created_at', width: 170, render: formatDate },
    { title: '', key: 'action', width: 56, render: (_, row) => <Button type="text" icon={<EyeOutlined />} aria-label={`Открыть письмо ${row.id}`} onClick={() => void openDetails(row.id)} /> },
  ]

  const changePage = (pagination: TablePaginationConfig) => setQuery((current) => ({ ...current, page: pagination.current, pageSize: pagination.pageSize }))

  const exportEmails = async () => {
    setExporting(true)
    try {
      const blob = await api.exportEmails(query)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'emails.csv'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch { message.error('Не удалось выгрузить отправки') }
    finally { setExporting(false) }
  }

  const retryEmail = async (id: number) => {
    setRetrying(true)
    try {
      await api.retryEmail(id)
      message.success('Письмо поставлено в очередь повторно')
      setDetails((await api.emailDetails(id)).data)
      await load()
    } catch { message.error('Не удалось повторить письмо. Проверьте баланс счёта.') }
    finally { setRetrying(false) }
  }

  return (
    <Flex vertical gap="middle">
      <PageHeader title="Отправки" description="Каждое письмо, его получатель и окончательный ответ Mailgun." icon={<MailOutlined />} extra={<Space wrap><Button icon={<DownloadOutlined />} loading={exporting} onClick={() => void exportEmails()}>Выгрузить CSV</Button><Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>Обновить</Button></Space>} />
      <Card className="mail-filters-card">
        <Flex gap={10} wrap>
          <Input allowClear prefix={<SearchOutlined />} placeholder="Получатель, тема или номер клиента" className="mail-search" onPressEnter={(event) => setQuery((current) => ({ ...current, page: 1, search: event.currentTarget.value.trim() || undefined }))} onClear={() => setQuery((current) => ({ ...current, page: 1, search: undefined }))} />
          <Select allowClear placeholder="Все состояния" options={emailStatusOptions} className="mail-status-filter" onChange={(status) => setQuery((current) => ({ ...current, page: 1, status }))} />
          <DatePicker.RangePicker
            className="mail-date-filter"
            format="DD.MM.YYYY"
            allowEmpty={[true, true]}
            presets={[
              { label: 'Сегодня', value: () => { const now = appTimezoneNow(); return [now, now] } },
              { label: 'Вчера', value: () => { const day = appTimezoneNow().subtract(1, 'day'); return [day, day] } },
              { label: 'Последние 7 дней', value: () => { const now = appTimezoneNow(); return [now.subtract(6, 'day'), now] } },
              { label: 'Последние 30 дней', value: () => { const now = appTimezoneNow(); return [now.subtract(29, 'day'), now] } },
            ]}
            placeholder={['Дата с', 'Дата по']}
            aria-label="Период создания"
            onChange={(dates) => setQuery((current) => ({
              ...current,
              page: 1,
              from: dates?.[0]?.startOf('day').utcOffset(getAppTimezoneOffsetMinutes(), true).toISOString(),
              before: dates?.[1]?.add(1, 'day').startOf('day').utcOffset(getAppTimezoneOffsetMinutes(), true).toISOString(),
            }))}
          />
          <Checkbox checked={Boolean(query.uniqueAddresses)} onChange={(event) => setQuery((current) => ({ ...current, page: 1, uniqueAddresses: event.target.checked || undefined }))}>Только уникальные адреса</Checkbox>
        </Flex>
      </Card>
      <Card className="table-card">
        <Table rowKey="id" columns={columns} dataSource={items} loading={loading} onChange={changePage} onRow={(row) => ({ onDoubleClick: () => void openDetails(row.id) })} scroll={{ x: 1050 }} pagination={{ current: query.page, pageSize: query.pageSize, total: count, showSizeChanger: true, showTotal: (total, range) => `${range[0]}–${range[1]} из ${total}` }} />
      </Card>
      <Drawer open={detailsLoading || Boolean(details)} loading={detailsLoading} size="large" title={details ? `Письмо №${details.email.id}` : 'Письмо'} onClose={() => {
        setDetails(null)
        setDetailsLoading(false)
        openedFromUrl.current = null
        if (searchParams.has('open')) {
          const next = new URLSearchParams(searchParams)
          next.delete('open')
          setSearchParams(next, { replace: true })
        }
      }}>
        {details ? <EmailDrawer details={details} canRetry={user?.role !== 'viewer'} retrying={retrying} onRetry={retryEmail} /> : !detailsLoading && <Empty />}
      </Drawer>
    </Flex>
  )
}

function EmailDrawer({ details, canRetry, retrying, onRetry }: { details: EmailDetails; canRetry: boolean; retrying: boolean; onRetry: (id: number) => Promise<void> }) {
  const email = details.email
  const retryable = canRetry && !email.mailgun_message_id && ['insufficient_balance', 'submission_failed', 'rejected'].includes(email.status)
  return (
    <Flex vertical gap="large">
      {retryable && <Button type="primary" icon={<RedoOutlined />} loading={retrying} onClick={() => void onRetry(email.id)}>Повторить отправку</Button>}
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Состояние"><StatusTag status={email.status} /></Descriptions.Item>
        <Descriptions.Item label="Получатель"><Typography.Text copyable>{email.email_address}</Typography.Text></Descriptions.Item>
        <Descriptions.Item label="Отправитель">{email.from_address}</Descriptions.Item>
        <Descriptions.Item label="Тема">{email.subject}</Descriptions.Item>
        <Descriptions.Item label="Приложение">{email.api_key_name || 'Удалено'}</Descriptions.Item>
        <Descriptions.Item label="Номер клиента">{email.client_reference || '—'}</Descriptions.Item>
        <Descriptions.Item label="Номер Mailgun"><Typography.Text copyable>{email.mailgun_message_id || 'Ещё не присвоен'}</Typography.Text></Descriptions.Item>
        <Descriptions.Item label="Стоимость">{email.price === null ? '—' : `${email.price.toFixed(6)} ${email.currency}`}</Descriptions.Item>
        <Descriptions.Item label="Создано">{formatDate(email.created_at)}</Descriptions.Item>
        {email.failure_reason && <Descriptions.Item label="Причина">{email.failure_reason}</Descriptions.Item>}
      </Descriptions>
      <div>
        <Typography.Title level={5}>Расчёты</Typography.Title>
        {details.transactions.length ? (
          <Flex vertical gap={6}>
            {details.transactions.map((transaction) => (
              <Flex key={transaction.id} justify="space-between" gap="middle">
                <Typography.Text>{transaction.operation_type === 2 ? 'Пополнение' : `Отправка, проводка №${transaction.id}`}</Typography.Text>
                <Space><Tag color={transaction.status === 2 ? 'success' : transaction.status === 1 ? 'default' : 'processing'}>{transaction.status === 2 ? 'Подтверждена' : transaction.status === 1 ? 'Отменена' : 'В резерве'}</Tag><Typography.Text>{Number(transaction.amount).toFixed(6)} {transaction.currency}</Typography.Text></Space>
              </Flex>
            ))}
          </Flex>
        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Проводок пока нет" />}
      </div>
      <div>
        <Typography.Title level={5}>История обработки</Typography.Title>
        {details.events.length ? (
          <Timeline items={details.events.map((event) => ({
            color: event.event === 'delivered' ? 'green' : event.severity === 'permanent' || event.event === 'rejected' ? 'red' : 'blue',
            content: <Flex vertical gap={2}><Typography.Text strong>{eventLabel(event.event, event.severity)}</Typography.Text><Typography.Text type="secondary">{formatDate(event.event_timestamp)}</Typography.Text>{event.reason && <Typography.Text>{reasonLabel(event.reason)}</Typography.Text>}</Flex>,
          }))} />
        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Mailgun ещё не прислал событий" />}
      </div>
      {(email.text_body || email.html_body) && <div><Typography.Title level={5}>Содержимое</Typography.Title><pre className="mail-body-preview">{email.text_body || email.html_body}</pre></div>}
    </Flex>
  )
}

function StatusTag({ status }: { status: EmailStatus }) {
  const view = emailStatusView[status]
  return <Tag color={view.color}>{view.label}</Tag>
}
function formatDate(value?: string | null) { return value ? formatInAppTimezone(value, 'DD.MM.YYYY HH:mm:ss') : '—' }
function eventLabel(event: string, severity: string | null) {
  if (event === 'accepted') return 'Mailgun принял письмо'
  if (event === 'delivered') return 'Сервер получателя принял письмо'
  if (event === 'rejected') return 'Mailgun отклонил письмо'
  if (event === 'failed' && severity === 'permanent') return 'Окончательная ошибка доставки'
  if (event === 'failed') return 'Временная ошибка доставки'
  return event
}
function reasonLabel(reason: string) {
  const labels: Record<string, string> = {
    bounce: 'Сервер получателя отклонил адрес',
    old: 'Mailgun исчерпал все повторные попытки',
    suppress: 'Адрес находится в списке запрета отправки',
  }
  return labels[reason.toLowerCase()] || reason
}
